import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaGift, FaCog, FaHome, FaSignOutAlt, FaShieldAlt, FaChevronDown, FaUsers, FaFingerprint } from 'react-icons/fa';
import { FaCircleDollarToSlot } from 'react-icons/fa6';
import { getUser, clearUser } from '../utils/authHelper';
import { usePermissions } from '../utils/PermissionContext';
import { usePresence, STATUS_COLOR, STATUS_LABEL } from '../utils/usePresence';

const NAV = [
  {
    section: null,
    items: [{ type: 'link', to: '/dashboard', icon: FaHome, label: 'Dashboard', perm: 'dashboard' }],
  },
  {
    section: 'Bonuslar',
    items: [{
      type: 'group', id: 'bonuslar', icon: FaGift, label: 'Bonus Talepleri',
      children: [
        { to: '/pending-requests',   label: 'Bekleyenler',   color: '#60a5fa', perm: 'bonus.pending'    },
        { to: '/completed-requests', label: 'Onaylananlar',  color: '#34d399', perm: 'bonus.completed'  },
        { to: '/rejected-requests',  label: 'Reddedilenler', color: '#f87171', perm: 'bonus.rejected'   },
      ],
    }],
  },
  {
    section: 'Sistem',
    items: [
      {
        type: 'group', id: 'ayarlar', icon: FaCog, label: 'Ayarlar',
        children: [
          { to: '/settings/bonuses', label: 'Bonus Yönetimi',  perm: 'settings.bonuses' },
          { to: '/domain-settings',  label: 'Domain Yönetimi', perm: 'settings.domain'  },
          { to: '/site-settings',    label: 'Genel Ayarlar',   perm: 'settings.site'    },
        ],
      },
      {
        type: 'group', id: 'musteri', icon: FaShieldAlt, label: 'Müşteri Yönetimi',
        children: [
          { to: '/verification-monitor',        label: 'Doğrulama Monitörü', color: '#a78bfa', perm: 'settings.verification' },
          { to: '/sms-yonetimi',                 label: 'SMS Merkezi',        color: '#3b82f6', perm: 'user.sms'       },
          { to: '/blacklist-settings',           label: 'Bonus Blacklist',    color: '#f87171', perm: 'user.blacklist' },
          { to: '/callmeblacklist-settings',     label: 'Beni Ara Blacklist', color: '#f87171', perm: 'user.callme'    },
        ],
      },
    ],
  },
  {
    section: 'Finansal',
    gold: true,
    items: [{
      type: 'group', id: 'finansal', icon: FaCircleDollarToSlot, label: 'Finansal Yönetim', gold: true,
      children: [
        { to: '/financial-dashboard', label: 'Finans Dashboard',   perm: 'financial.dashboard' },
        { to: '/kasa-yonetimi',       label: 'Kasa Takip',         perm: 'financial.kasa'      },
        { to: '/gunluk-hareketler',   label: 'Günlük Hareketler',  perm: 'financial.gunluk'    },
        { to: '/odeme-yonetimi',      label: 'Ödemeler',           perm: 'financial.odeme'     },
        { to: '/tahsilat-yonetimi',   label: 'Tahsilat',           perm: 'financial.tahsilat'  },
        { to: '/virman-yonetimi',     label: 'Virman',             perm: 'financial.virman'    },
        { to: '/odeme-yontemleri',    label: 'Ödeme Yöntemleri',   perm: 'financial.yontemler' },
        { to: '/komisyon-hesaplama',   label: 'Komisyon Hesaplama', perm: 'financial.commission' },
      ],
    }],
  },
  {
    section: 'Yönetim',
    items: [{
      type: 'link', to: '/user-management', icon: FaUsers, label: 'Kullanıcı Yönetimi', perm: 'user.management',
    }],
  },
];

/* ── Status dot bileşeni ── */
const StatusDot = ({ status, size = 10, style = {} }) => (
  <span style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
    background: STATUS_COLOR[status] || STATUS_COLOR.offline,
    boxShadow: status === 'online'
      ? `0 0 0 2px #090a0c, 0 0 8px ${STATUS_COLOR.online}99`
      : status === 'idle'
      ? `0 0 0 2px #090a0c, 0 0 6px ${STATUS_COLOR.idle}77`
      : '0 0 0 2px #090a0c',
    transition: 'background .4s, box-shadow .4s',
    ...style,
  }} />
);

/* ── Online kullanıcı kartı ── */
const OnlinePanel = ({ onlineUsers, myId }) => {
  const [open, setOpen] = useState(false);
  const onlineCount = onlineUsers.filter(u => u.computedStatus === 'online').length;
  const idleCount   = onlineUsers.filter(u => u.computedStatus === 'idle').length;
  const total       = onlineUsers.length;

  if (total === 0) return null;

  return (
    <div style={{ marginBottom: 7 }}>
      {/* Başlık / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 11px', borderRadius: 10,
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        {/* Avatar yığını */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {onlineUsers.slice(0, 3).map((u, i) => {
            const initials = (u.displayName || u.username || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
            return (
              <div key={u._id} style={{
                width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                background: u.isSuperAdmin
                  ? 'linear-gradient(135deg,rgba(167,139,250,.5),rgba(167,139,250,.15))'
                  : 'linear-gradient(135deg,rgba(242,113,39,.4),rgba(242,113,39,.1))',
                border: `1px solid ${u.isSuperAdmin ? 'rgba(167,139,250,.4)' : 'rgba(242,113,39,.3)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: u.isSuperAdmin ? '#a78bfa' : '#f27127',
                fontSize: '.45rem', fontWeight: 700,
                marginLeft: i > 0 ? -5 : 0,
                boxShadow: '0 0 0 1.5px #090a0c',
                position: 'relative', zIndex: 3 - i,
              }}>{initials}</div>
            );
          })}
          {total > 3 && (
            <div style={{
              width: 20, height: 20, borderRadius: 6, flexShrink: 0,
              background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,.35)', fontSize: '.42rem', fontWeight: 700,
              marginLeft: -5, boxShadow: '0 0 0 1.5px #090a0c',
            }}>+{total - 3}</div>
          )}
        </div>

        {/* Sayaç */}
        <div style={{ flex: 1, textAlign: 'left' }}>
          <span style={{ fontSize: '.68rem', fontWeight: 600, color: 'rgba(255,255,255,.45)' }}>
            <span style={{ color: STATUS_COLOR.online, fontWeight: 700 }}>{onlineCount}</span>
            {idleCount > 0 && <span style={{ color: STATUS_COLOR.idle }}> · {idleCount} boşta</span>}
            <span style={{ color: 'rgba(255,255,255,.2)' }}> çevrimiçi</span>
          </span>
        </div>

        <FaChevronDown style={{
          fontSize: '.5rem', color: 'rgba(255,255,255,.2)', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform .25s',
        }} />
      </button>

      {/* Liste */}
      <div style={{
        maxHeight: open ? onlineUsers.length * 36 + 8 : 0,
        overflow: 'hidden',
        transition: 'max-height .3s cubic-bezier(.65,0,.35,1)',
      }}>
        <div style={{ padding: '2px 8px 6px' }}>
          {onlineUsers.map(u => {
            const initials = (u.displayName || u.username || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
            const isMe = u._id === myId;
            return (
              <div key={u._id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 6px', borderRadius: 8,
                background: isMe ? 'rgba(255,255,255,.03)' : 'transparent',
              }}>
                {/* Mini avatar */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 7,
                    background: u.isSuperAdmin
                      ? 'linear-gradient(135deg,rgba(167,139,250,.3),rgba(167,139,250,.08))'
                      : 'linear-gradient(135deg,rgba(242,113,39,.3),rgba(242,113,39,.08))',
                    border: `1px solid ${u.isSuperAdmin ? 'rgba(167,139,250,.25)' : 'rgba(242,113,39,.25)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: u.isSuperAdmin ? '#a78bfa' : '#f27127',
                    fontSize: '.52rem', fontWeight: 700,
                  }}>{initials}</div>
                  <StatusDot
                    status={u.computedStatus}
                    size={8}
                    style={{ position: 'absolute', bottom: -1, right: -1 }}
                  />
                </div>
                {/* İsim */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '.72rem', fontWeight: isMe ? 600 : 400,
                    color: isMe ? '#e2e8f0' : 'rgba(255,255,255,.45)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {u.displayName || u.username}{isMe && <span style={{ color: 'rgba(255,255,255,.2)', fontWeight: 400 }}> (sen)</span>}
                  </div>
                  {u.title && (
                    <div style={{ fontSize: '.58rem', color: 'rgba(255,255,255,.2)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.title}
                    </div>
                  )}
                </div>
                {/* Status badge */}
                <span style={{
                  fontSize: '.55rem', fontWeight: 600, flexShrink: 0,
                  color: STATUS_COLOR[u.computedStatus],
                  opacity: .8,
                }}>
                  {u.computedStatus === 'idle' ? 'boşta' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   ANA SIDEBAR
════════════════════════════════════════ */
/* ── Session Countdown Hook ── */
const useSessionCountdown = () => {
  const [remaining, setRemaining] = useState(null); // saniye

  useEffect(() => {
    const calc = () => {
      const token = localStorage.getItem('token');
      if (!token) { setRemaining(null); return; }
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const diff = Math.floor(payload.exp - Date.now() / 1000);
        setRemaining(diff > 0 ? diff : 0);
      } catch { setRemaining(null); }
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);

  return remaining;
};

const fmtTime = (s) => {
  if (s === null) return null;
  if (s <= 0) return '00:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const Sidebar = () => {
  const location = useLocation();
  const [open, setOpen] = useState({ bonuslar: true, finansal: true });
  const user = getUser();
  const { permissions, isSuperAdmin } = usePermissions();
  const { onlineUsers, myStatus, goOffline } = usePresence();

  const canSee = (perm) => {
    if (!perm) return true;
    return isSuperAdmin || permissions.includes(perm);
  };

  useEffect(() => {
    const p = location.pathname;
    const up = {};
    if (p.includes('requests'))                                                            up.bonuslar  = true;
    if (p.startsWith('/settings') || p.includes('domain') || p.includes('site'))          up.ayarlar   = true;
    if (p.includes('blacklist'))                                                           up.kullanici = true;
    if (['financial','kasa','gunluk','odeme','tahsilat','virman'].some(k=>p.includes(k))) up.finansal  = true;
    if (Object.keys(up).length) setOpen(o => ({ ...o, ...up }));
  }, [location.pathname]);

  const isActive  = (to)  => location.pathname === to;
  const hasActive = (arr) => arr?.some(c => isActive(c.to));
  const toggle    = (id)  => setOpen(o => ({ ...o, [id]: !o[id] }));
  const initials  = (name = '') =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'AD';

  const sessionRemaining = useSessionCountdown();
  const sessionUrgent = sessionRemaining !== null && sessionRemaining < 1800; // 30dk altı kırmızı
  const sessionWarn   = sessionRemaining !== null && sessionRemaining < 7200; // 2sa altı sarı

  const handleLogout = async () => {
    goOffline();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {}
    clearUser();
    window.location.href = '/login';
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        .sb { font-family: 'Outfit', sans-serif; }
        .sb-nav::-webkit-scrollbar { width: 0px }
        .sb-nav { scrollbar-width: none }
        .sb-row {
          display: flex; align-items: center; gap: 11px;
          padding: 10px 14px; border-radius: 12px;
          cursor: pointer; user-select: none;
          font-size: .84rem; font-weight: 500;
          transition: background .15s, color .15s, transform .12s;
          text-decoration: none; position: relative;
        }
        .sb-row:hover { transform: translateX(2px); }
        .sb-sub {
          display: flex; align-items: center; gap: 10px;
          padding: 7px 12px 7px 14px; border-radius: 9px;
          font-size: .8rem; font-weight: 400;
          text-decoration: none; cursor: pointer;
          transition: background .14s, color .14s, padding-left .14s;
        }
        .sb-sub:hover { padding-left: 18px !important; }
        @keyframes subDrop {
          from { opacity:0; transform: translateY(-6px); }
          to   { opacity:1; transform: none; }
        }
        @keyframes pulse-online {
          0%, 100% { opacity: 1; }
          50%       { opacity: .55; }
        }
        .sb-logout { transition: all .15s; }
        .sb-logout:hover {
          background: rgba(239,68,68,.15) !important;
          color: #ef4444 !important;
          border-color: rgba(239,68,68,.35) !important;
        }
      `}</style>

      <div className="sb" style={{
        width: 268, height: '100vh',
        position: 'fixed', top: 0, left: 0,
        display: 'flex', flexDirection: 'column',
        zIndex: 1000,
        background: '#090a0c',
        boxShadow: 'inset -1px 0 0 rgba(255,255,255,.06)',
      }}>

        {/* ── Brand ── */}
        <div style={{
          padding: '20px 16px 16px', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid rgba(255,255,255,.05)',
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg, #f27127 0%, #d4560f 50%, #b83e00 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 1px rgba(242,113,39,.5), 0 4px 20px rgba(242,113,39,.35)',
          }}>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: '.95rem', letterSpacing: '-1px' }}>A</span>
          </div>
          <div>
            <div style={{ color: '#f0eeff', fontWeight: 800, fontSize: '.95rem', letterSpacing: '-.3px', lineHeight: 1 }}>AntikBet</div>
            <div style={{ color: 'rgba(242,113,39,.5)', fontSize: '.6rem', fontWeight: 500, marginTop: 3 }}>Admin Panel</div>
          </div>
        </div>

        {/* ── Nav ── */}
        <nav className="sb-nav" style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
          {NAV.map((block, bi) => {
            const visibleItems = block.items.filter(item => {
              if (item.type === 'link') return canSee(item.perm);
              if (item.type === 'group') return item.children.some(ch => canSee(ch.perm));
              return false;
            });
            if (!visibleItems.length) return null;

            return (
              <div key={bi} style={{ marginBottom: 4 }}>
                {block.section && (
                  <div style={{
                    padding: '10px 14px 5px',
                    fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '1.7px',
                    color: block.gold ? 'rgba(245,158,11,.28)' : 'rgba(255,255,255,.12)',
                  }}>{block.section}</div>
                )}

                {visibleItems.map(item => {
                  if (item.type === 'link') {
                    const active = isActive(item.to);
                    const Icon = item.icon;
                    return (
                      <Link key={item.to} to={item.to} className="sb-row" style={{
                        color: active ? '#fff' : 'rgba(255,255,255,.38)',
                        fontWeight: active ? 600 : 400,
                        background: active
                          ? 'linear-gradient(90deg, rgba(242,113,39,.18) 0%, rgba(242,113,39,.05) 100%)'
                          : 'transparent',
                      }}>
                        {active && <span style={{
                          position: 'absolute', left: 0, top: '15%', bottom: '15%',
                          width: 3, borderRadius: '0 3px 3px 0',
                          background: 'linear-gradient(180deg, #ff8c42, #f27127)',
                          boxShadow: '0 0 10px rgba(242,113,39,.8)',
                        }} />}
                        <div style={{
                          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: active ? 'rgba(242,113,39,.2)' : 'rgba(255,255,255,.05)',
                          border: active ? '1px solid rgba(242,113,39,.3)' : '1px solid rgba(255,255,255,.06)',
                          transition: 'all .15s',
                        }}>
                          <Icon style={{ color: active ? '#f27127' : 'rgba(255,255,255,.3)', fontSize: '.78rem' }} />
                        </div>
                        {item.label}
                      </Link>
                    );
                  }

                  const gold   = !!item.gold;
                  const accent = gold ? '#f59e0b' : '#f27127';
                  const acRgb  = gold ? '245,158,11' : '242,113,39';
                  const isOpen = !!open[item.id];
                  const active = hasActive(item.children);
                  const Icon   = item.icon;

                  return (
                    <div key={item.id}>
                      <div className="sb-row" onClick={() => toggle(item.id)} style={{
                        color: active ? (isOpen ? accent : '#fff') : 'rgba(255,255,255,.38)',
                        fontWeight: active ? 600 : 400,
                        background: active && !isOpen
                          ? `linear-gradient(90deg, rgba(${acRgb},.15) 0%, rgba(${acRgb},.04) 100%)`
                          : 'transparent',
                      }}>
                        {active && !isOpen && <span style={{
                          position: 'absolute', left: 0, top: '15%', bottom: '15%',
                          width: 3, borderRadius: '0 3px 3px 0',
                          background: `linear-gradient(180deg, ${accent}cc, ${accent})`,
                          boxShadow: `0 0 10px ${accent}cc`,
                        }} />}
                        <div style={{
                          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: active ? `rgba(${acRgb},.15)` : 'rgba(255,255,255,.05)',
                          border: active ? `1px solid rgba(${acRgb},.28)` : '1px solid rgba(255,255,255,.06)',
                          transition: 'all .15s',
                        }}>
                          <Icon style={{ color: active ? accent : 'rgba(255,255,255,.3)', fontSize: '.78rem' }} />
                        </div>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        <FaChevronDown style={{
                          fontSize: '.58rem', flexShrink: 0,
                          color: isOpen ? accent : 'rgba(255,255,255,.2)',
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform .3s cubic-bezier(.65,0,.35,1), color .2s',
                        }} />
                      </div>

                      <div style={{
                        maxHeight: isOpen ? item.children.length * 38 + 12 : 0,
                        overflow: 'hidden',
                        transition: 'max-height .36s cubic-bezier(.65,0,.35,1)',
                      }}>
                        <div style={{ padding: '2px 0 4px 6px', position: 'relative' }}>
                          <div style={{
                            position: 'absolute', left: 20, top: 4, bottom: 4, width: 1,
                            background: `linear-gradient(180deg, transparent, rgba(${acRgb},.18) 20%, rgba(${acRgb},.18) 80%, transparent)`,
                          }} />
                          {item.children.filter(ch => canSee(ch.perm)).map((ch, ci) => {
                            const ca = isActive(ch.to);
                            const hasColor = !!ch.color;
                            const dotColor = hasColor ? ch.color : (ca ? accent : 'rgba(255,255,255,.18)');
                            return (
                              <Link key={ch.to} to={ch.to} className="sb-sub" style={{
                                color: ca ? '#fff' : 'rgba(255,255,255,.32)',
                                fontWeight: ca ? 600 : 400,
                                background: ca ? `rgba(${acRgb},.1)` : 'transparent',
                                paddingLeft: 14,
                                animation: isOpen ? `subDrop .2s ease ${ci * 40}ms both` : 'none',
                              }}>
                                <span style={{
                                  width: 6, height: 6, borderRadius: '50%',
                                  background: dotColor,
                                  boxShadow: (ca && !hasColor) ? `0 0 8px ${dotColor}` : 'none',
                                  flexShrink: 0, display: 'inline-block',
                                  transition: 'all .2s',
                                }} />
                                {ch.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* ── Footer ── */}
        <div style={{
          padding: '8px 10px 14px', flexShrink: 0,
          borderTop: '1px solid rgba(255,255,255,.05)',
        }}>

          {/* Online kullanıcı paneli */}
          <OnlinePanel onlineUsers={onlineUsers} myId={user?.id} />

          {/* Profil kartı */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 11px', borderRadius: 12, marginBottom: 7,
            background: 'rgba(255,255,255,.03)',
            border: '1px solid rgba(255,255,255,.06)',
          }}>
            {/* Avatar + status dot */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'linear-gradient(135deg, rgba(242,113,39,.3), rgba(242,113,39,.08))',
                border: '1px solid rgba(242,113,39,.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#f27127', fontWeight: 700, fontSize: '.68rem',
                boxShadow: '0 0 12px rgba(242,113,39,.12)',
              }}>{initials(user?.displayName || user?.username || '')}</div>

              {/* Status dot — avatar köşesinde */}
              <StatusDot
                status={myStatus}
                size={10}
                style={{
                  position: 'absolute', bottom: -2, right: -2,
                  animation: myStatus === 'online' ? 'pulse-online 2.5s ease-in-out infinite' : 'none',
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '.79rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.displayName || user?.username || 'Kullanıcı'}
              </div>
              {/* Ünvan + status etiketi */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <span style={{ color: isSuperAdmin ? 'rgba(167,139,250,.7)' : 'rgba(242,113,39,.55)', fontSize: '.59rem', fontWeight: 500 }}>
                  {isSuperAdmin ? '👑 Super Admin' : (user?.title || 'Operatör')}
                </span>
                <span style={{ color: 'rgba(255,255,255,.1)', fontSize: '.55rem' }}>·</span>
                <span style={{ fontSize: '.59rem', fontWeight: 600, color: STATUS_COLOR[myStatus] }}>
                  {STATUS_LABEL[myStatus]}
                </span>
              </div>
            </div>
          </div>

          {/* ── Session countdown ── */}
          {sessionRemaining !== null && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 11px', borderRadius: 10, marginBottom: 7,
              background: sessionUrgent ? 'rgba(239,68,68,.06)' : sessionWarn ? 'rgba(251,191,36,.05)' : 'rgba(255,255,255,.02)',
              border: `1px solid ${sessionUrgent ? 'rgba(239,68,68,.15)' : sessionWarn ? 'rgba(251,191,36,.12)' : 'rgba(255,255,255,.05)'}`,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: sessionUrgent ? '#f87171' : sessionWarn ? '#fbbf24' : '#10b981',
                boxShadow: `0 0 6px ${sessionUrgent ? '#f87171' : sessionWarn ? '#fbbf24' : '#10b981'}`,
                animation: sessionUrgent ? 'pulse-online 1s ease-in-out infinite' : 'none',
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.57rem', color: 'rgba(255,255,255,.25)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 2 }}>Oturum Süresi</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '.78rem', fontWeight: 600, color: sessionUrgent ? '#f87171' : sessionWarn ? '#fbbf24' : 'rgba(255,255,255,.5)', letterSpacing: '1px' }}>
                  {fmtTime(sessionRemaining)}
                </div>
              </div>
              {sessionUrgent && (
                <div style={{ fontSize: '.55rem', color: '#f87171', fontWeight: 700, textAlign: 'right', lineHeight: 1.3 }}>
                  Süre<br/>Doluyor
                </div>
              )}
            </div>
          )}

          <button className="sb-logout" onClick={handleLogout} style={{
            width: '100%', padding: '9px 14px', borderRadius: 10,
            border: '1px solid rgba(239,68,68,.1)',
            background: 'rgba(239,68,68,.04)',
            color: 'rgba(239,68,68,.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontSize: '.78rem', fontWeight: 600, cursor: 'pointer',
            fontFamily: "'Outfit', sans-serif", outline: 'none',
          }}>
            <FaSignOutAlt style={{ fontSize: '.8rem' }} />
            Güvenli Çıkış
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
